// client/components/ui/DatePickers.jsx
/**
 * CONTRACT (Single Source of Truth für Datumseingaben):
 * - DateOnlyField: value + onChange sind IMMER "YYYY-MM-DD" (oder "").
 *   Nie new Date(ISO).toISOString().slice(0,10) – Timezone-Off-by-one vermeiden.
 * - DateTimeField: value + onChange sind IMMER "YYYY-MM-DDTHH:mm" (oder "").
 * - Nur diese Datei darf <input type="date"> / <input type="datetime-local"> verwenden (Mobile-Branch).
 * - Parsing/Normalisierung ausschließlich hier; keine verstreute Logik in Komponenten.
 */
import React, { useMemo, useId, useState, useRef } from "react";
import DatePicker from "react-datepicker";
import { de as deLocale } from "date-fns/locale";
import { Calendar, X } from "lucide-react";
import AppButton from "./AppButton.jsx";
import { Select as FormSelect } from "../forms/shared/FormAtoms.jsx";

const pad = (n) => String(n).padStart(2, "0");

/** Lokales YYYY-MM-DD aus einer Date. */
const toYMD = (d) =>
  d && !Number.isNaN(d.getTime())
    ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    : "";

/** YYYY-MM-DDTHH:mm aus Date (lokal). */
const toLocalIsoMinute = (d) =>
  d && !Number.isNaN(d.getTime())
    ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
    : "";

/* ───────── Validatoren / Normalizer (Contract) ───────── */

/** Prüft, ob s ein gültiges YYYY-MM-DD ist. */
export function isValidYMD(s) {
  if (typeof s !== "string" || !s) return false;
  const m = s.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return false;
  const y = Number(m[1]);
  const M = Number(m[2]);
  const d = Number(m[3]);
  const x = new Date(y, M - 1, d);
  return x.getFullYear() === y && x.getMonth() === M - 1 && x.getDate() === d;
}

/**
 * Normalisiert zu "YYYY-MM-DD" oder "".
 * DateOnly: NIEMALS über new Date(ISO) shiften – bei ISO/DateTime nur Datumsteil slice(0,10).
 */
export function normalizeYMD(v) {
  if (v == null || v === "") return "";
  if (v instanceof Date) return toYMD(v);
  const s = String(v).trim();
  if (!s) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  if (/^\d{4}-\d{2}-\d{2}[T ]/.test(s) || /Z$/i.test(s)) return s.slice(0, 10);
  const dm = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{2,4})$/);
  if (dm) {
    const dd = Number(dm[1]);
    const MM = Number(dm[2]);
    const y = dm[3].length === 2
      ? (Number(dm[3]) <= 29 ? 2000 + Number(dm[3]) : 1900 + Number(dm[3]))
      : Number(dm[3]);
    const d = new Date(y, MM - 1, dd, 0, 0, 0, 0);
    return Number.isNaN(d.getTime()) ? "" : toYMD(d);
  }
  return "";
}

/** Prüft, ob s "YYYY-MM-DDTHH:mm" ist. */
export function isValidLocalIsoMinute(s) {
  if (typeof s !== "string" || !s) return false;
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(s.trim());
}

/**
 * Normalisiert zu "YYYY-MM-DDTHH:mm" oder "".
 * Akzeptiert "YYYY-MM-DDTHH:mm" direkt; ISO mit Z → lokale Minute.
 */
export function normalizeLocalIsoMinute(v) {
  if (v == null || v === "") return "";
  const s = String(v).trim().replace(" ", "T");
  if (!s) return "";
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(s)) return s;
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s) || /Z$/.test(s)) {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? "" : toLocalIsoMinute(d);
  }
  const d = parseDateTimeFlexible(s);
  return d ? toLocalIsoMinute(d) : "";
}

/** Heute als YYYY-MM-DD (lokal). */
export function todayYMD() {
  return toYMD(new Date());
}

/** Datum vor N Tagen als YYYY-MM-DD (lokal). */
export function ymdMinusDays(days) {
  const d = new Date();
  d.setDate(d.getDate() - Number(days || 0));
  return toYMD(d);
}

/* ───────── Helpers ───────── */
export const Label = ({ children, htmlFor, className = "" }) => (
  <label
    className={`block text-xs uppercase tracking-wide mb-1 wrtk-label ${className}`}
    htmlFor={htmlFor}
  >
    {children}
  </label>
);

/* Parsing (DE + ISO) */
const parseDateFlexible = (v) => {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v) ? null : v;
  const s = String(v).trim();

  // WICHTIG: Volle ISO-Form mit Zeit/Z → Browser soll korrekt parsen (TZ beachten)
  if (/[T ]\d{2}:\d{2}/.test(s) || /Z$/.test(s)) {
    const d = new Date(s);
    return isNaN(d) ? null : d; // echte Date inkl. TZ
  }

  let m = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{2,4})$/);
  if (m) {
    const dd = +m[1];
    const MM = +m[2];
    const y =
      m[3].length === 2
        ? +m[3] <= 29
          ? 2000 + +m[3]
          : 1900 + +m[3]
        : +m[3];
    const d = new Date(y, MM - 1, dd, 0, 0, 0, 0);
    return isNaN(d) ? null : d;
  }

  m = s.match(/^(\d{2})(\d{2})(\d{4})$/);
  if (m) return new Date(+m[3], +m[2] - 1, +m[1], 0, 0, 0, 0);

  const [y, M, d] = s.slice(0, 10).split("-").map(Number);
  if (y && M && d) {
    const dt = new Date(y, M - 1, d, 0, 0, 0, 0);
    return isNaN(dt) ? null : dt;
  }
  return null;
};

const parseDateTimeFlexible = (v) => {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v) ? null : v;
  const s = String(v).trim().replace(" ", "T");

  let m = s.match(
    /^(\d{1,2})\.(\d{1,2})\.(\d{2,4})[ T](\d{1,2}):(\d{2})$/
  );
  if (m) {
    const dd = +m[1];
    const MM = +m[2];
    const y =
      m[3].length === 2
        ? +m[3] <= 29
          ? 2000 + +m[3]
          : 1900 + +m[3]
        : +m[3];
    const hh = +m[4];
    const mm = +m[5];
    const dt = new Date(y, MM - 1, dd, hh, mm, 0, 0);
    return isNaN(dt) ? null : dt;
  }

  const [date, time = "00:00"] = s.split("T");
  if (date?.includes("-")) {
    const [y, M, d] = date.split("-").map(Number);
    const [hh = 0, mm = 0] = time.split(":").map(Number);
    const dt = new Date(y, (M || 1) - 1, d || 1, hh, mm, 0, 0);
    return isNaN(dt) ? null : dt;
  }

  return parseDateFlexible(s);
};

/* Device */
const isMobileDevice = () => {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(pointer: coarse), (max-width: 992px)").matches;
};

function useStableMobileDateMode() {
  // Stabil pro Mount: verhindert Wechsel zwischen native/react-datepicker
  // mitten in offenen Formularen bei Resize/Viewport-Sprüngen.
  const [isMobile] = useState(() => isMobileDevice());
  return isMobile;
}

/** react-datepicker v8: Popper per Portal an document.body — sonst hinter Modal-Footern / durch overflow-hidden */
const DATEPICKER_POPPER_PORTAL_ID = "wrtk-datepicker-popper-root";
const DE_DAY_SHORT = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
const dePickerLocale = {
  ...deLocale,
  localize: {
    ...deLocale.localize,
    day: (day) => {
      return DE_DAY_SHORT[day] || "";
    },
  },
};

/* ───────── Styles (Glass, middle ground) ───────── */
function StyleOverrides() {
  return (
    <style>{`
      :root{
        --glass-bg: rgba(12,18,34,.68);
        --glass-edge: rgba(146,197,253,.20);
        --glass-inner: rgba(59,130,246,.12);
        --txt: #dbeafe;
        --muted: rgba(191,219,254,.85);
        --radius: 14px;

        --head-h: 52px;
        --dow-h: 22px;
        --today-h: 34px;
        --col: 1.9rem;
        --gap: .18rem;
        --ui: 12px;
        --padX: 10px;
      }

      .rd-popper, .rd-popper * {
        box-sizing: border-box;
        font-size: var(--ui) !important;
        pointer-events: auto !important;
      }
      .rd-popper{ z-index: 2147483647 !important; }
      /* Portal-Wurzel über App-Modals (ModalOrSheet z-index 9000) */
      #wrtk-datepicker-popper-root { z-index: 9600; position: relative; }
      .react-datepicker__triangle{ display:none !important; }

      /* Screenreader-Hinweis nicht sichtbar rendern (sonst „Selected date…“ über dem Feld) */
      .react-datepicker__aria-live,
      .wrtk-field-shell .react-datepicker__aria-live{
        position: absolute !important;
        width: 1px !important;
        height: 1px !important;
        padding: 0 !important;
        margin: -1px !important;
        overflow: hidden !important;
        clip: rect(0, 0, 0, 0) !important;
        white-space: nowrap !important;
        border: 0 !important;
      }

      .rd-cal.react-datepicker{
        position:relative;
        color: var(--txt) !important;
        background: #0b1324 !important;
        border: 1px solid var(--glass-edge) !important;
        border-radius: var(--radius) !important;
        box-shadow: 0 8px 24px rgba(0,0,0,.55), inset 0 1px 0 var(--glass-inner) !important;
        backdrop-filter: none;
        overflow: hidden !important;
      }

      /* Header – zentriert: Pfeile – Monat – Jahr */
      .react-datepicker__header{
        min-height: var(--head-h) !important;
        border:none !important;
        padding: 6px var(--padX) !important;
        display:flex; align-items:center; justify-content:center;
        background: linear-gradient(180deg, rgba(255,255,255,.06), rgba(255,255,255,.015)) !important;
      }
      .rd-head{
        display:inline-flex; align-items:center; gap:.45rem;
        border:1px solid rgba(255,255,255,.10);
        border-radius:12px;
        padding:2px 6px;
        background: rgba(255,255,255,.04);
      }
      .rd-head .btn{
        padding:3px 7px;
        border-radius:9px;
        background: transparent;
        color: var(--txt);
      }
      .rd-head .btn:hover{
        background: rgba(255,255,255,.08);
      }

      .react-datepicker__day-names{
        display:flex !important;
        justify-content:center !important;
        align-items:center !important;
        gap: var(--gap) !important;
        height:auto !important;
        padding:0 var(--padX) 6px !important;
        border-bottom:1px solid rgba(255,255,255,.08) !important;
      }
      .react-datepicker__day-name{
        width: var(--col) !important;
        max-width: var(--col) !important;
        margin:0 !important;
        overflow:hidden !important;
        white-space:nowrap !important;
        text-align:center !important;
        color: var(--muted);
        font-weight:500;
      }

      .react-datepicker__month{
        padding:7px var(--padX) 9px var(--padX) !important;
        display:block !important;
        grid-template-columns:none !important;
      }
      .react-datepicker__week{
        display:flex !important;
        gap: var(--gap) !important;
        justify-content:center !important;
      }

      .react-datepicker__day{
        width: var(--col) !important;
        height: var(--col) !important;
        margin:0 !important;
        border-radius: 10px !important;
        display:flex; align-items:center; justify-content:center;
        color: var(--txt) !important;
        border: 1px solid rgba(255,255,255,.06) !important;
        background: rgba(255,255,255,.04) !important;
        transition: background .12s, border-color .12s, box-shadow .12s, color .12s;
      }
      .react-datepicker__day:hover{
        background: rgba(255,255,255,.08) !important;
      }
      .react-datepicker__day--today{
        box-shadow: 0 0 0 1px rgba(59,130,246,.35) inset !important;
      }
      .react-datepicker__day--selected,
      .react-datepicker__day--keyboard-selected{
        color:#fff !important;
        background: linear-gradient(135deg, rgba(59,130,246,.32), rgba(59,130,246,.18)) !important;
        border-color: rgba(147,197,253,.45) !important;
        box-shadow: 0 0 6px rgba(59,130,246,.22) !important;
      }
      .react-datepicker__day--in-range,
      .react-datepicker__day--in-selecting-range{
        color: var(--txt) !important;
        background: rgba(59,130,246,.14) !important;
        border-color: rgba(147,197,253,.22) !important;
      }
      .react-datepicker__day--range-start,
      .react-datepicker__day--range-end,
      .react-datepicker__day--selecting-range-start,
      .react-datepicker__day--selecting-range-end{
        color:#fff !important;
        background: linear-gradient(135deg, rgba(59,130,246,.32), rgba(59,130,246,.18)) !important;
        border-color: rgba(147,197,253,.45) !important;
      }
      .react-datepicker__day--outside-month{
        opacity:.35 !important;
      }

      /* DateOnly – Heute mittig */
      .rd-cal .react-datepicker__today-button{
        position:absolute !important;
        left:0; right:0; bottom:0;
        height: var(--today-h) !important;
        background: rgba(59,130,246,.10) !important;
        border-top:1px solid rgba(255,255,255,.10) !important;
        font-weight:600;
        color: var(--txt) !important;
        display:flex; align-items:center; justify-content:center;
        cursor: pointer !important;
        pointer-events: auto !important;
        z-index:3;
      }

      /* DateOnly/Week/Month: Platz fuer Heute-Leiste reservieren, damit Tage sie nicht ueberdecken */
      .rd--dateonly .rd-cal{
        padding-bottom: var(--today-h) !important;
      }

      /* DateTime – Fußleiste: Heute links, Zeit rechts */
      .rd--dtbar .rd-cal{
        padding-bottom: var(--today-h) !important;
      }
      .rd--dtbar .react-datepicker__today-button{
        position:absolute !important;
        bottom:0 !important;
        left: var(--padX) !important;
        right:auto !important;
        width:auto !important;
        height: var(--today-h) !important;
        padding:0 .6rem !important;
        background:transparent !important;
        border-top:none !important;
        display:flex !important; align-items:center !important; justify-content:flex-start !important;
        z-index:3 !important;
      }
      .rd--dtbar .react-datepicker__input-time-container{
        position:absolute !important;
        bottom:0 !important;
        right: var(--padX) !important;
        height: var(--today-h) !important;
        display:flex !important; align-items:center !important; justify-content:flex-end !important;
        gap:.5rem !important;
        padding:0 !important;
        background:transparent !important;
        border-top:none !important;
        z-index:3 !important;
      }
      .rd--dtbar .react-datepicker__input-time{ padding:0 !important; }
      .rd--dtbar .react-datepicker-time__caption{ display:none !important; }
      .rd--dtbar .react-datepicker__time-container{ display:none !important; }

      .wrtk-time-input{
        width:92px;
        height:26px;
        text-align:center;
        border-radius:8px;
        background:rgba(12,18,34,.65);
        color:var(--txt);
        border:1px solid rgba(255,255,255,.18);
        padding:2px 8px;
        font-size: 11px;
      }
      .wrtk-time-input:focus{
        outline:none;
        box-shadow:0 0 0 2px rgba(96,165,250,.35);
        border-color:rgba(96,165,250,.35);
      }

      /* Eingabefeld (Glass) – einheitlich */
      .wrtk-field-shell{
        width: 100%;
        max-width: 100%;
        min-width: 0;
      }
      .wrtk-datefield-shell-inner{
        min-width: 0;
        max-width: 100%;
        overflow: hidden;
        border-radius: 14px;
      }
      .wrtk-datefield--compact .wrtk-datefield-shell-inner{
        border-radius: 0.375rem;
      }
      .wrtk-input{
        box-sizing: border-box;
        width:100%;
        max-width: 100%;
        min-width: 0;
        min-height: 2.45rem;
        padding: .48rem 5rem .48rem .8rem;
        color: var(--txt);
        background: linear-gradient(180deg, rgba(12,18,34,.55), rgba(12,18,34,.78));
        border:1px solid var(--glass-edge);
        border-radius: 14px;
        backdrop-filter: blur(8px);
        font-size: 0.8rem;
      }
      @media (max-width: 767px) {
        .wrtk-field-shell .wrtk-input{
          padding-right: 3.35rem;
          font-size: 16px;
        }
        .wrtk-datefield--compact .wrtk-input{
          padding-right: 3rem;
          font-size: 0.875rem;
        }
      }
      .wrtk-input:focus{
        outline:none;
        box-shadow: 0 0 0 2px rgba(96,165,250,.35);
        border-color: rgba(96,165,250,.35);
      }

      /* Kompakte Zeile (z. B. Stunden & Spesen): gleiche ungefähre Höhe/Radius wie benachbarte Selects */
      .wrtk-datefield--compact .wrtk-input{
        min-height: 2.75rem;
        padding: 0.5rem 3.65rem 0.5rem 0.75rem;
        border-radius: 0.375rem;
        font-size: 0.875rem;
        line-height: 1.25rem;
        background: #0b1324;
        border: 1px solid rgba(255,255,255,0.1);
        backdrop-filter: none;
      }
      @media (min-width: 768px) {
        .wrtk-datefield--compact .wrtk-input{
          min-height: 2.5rem;
        }
      }
      .wrtk-datefield--compact .wrtk-input:focus{
        border-color: rgba(96,165,250,.45);
      }
      .wrtk-datefield--compact .wrtk-label{
        margin-bottom: 0.25rem;
        font-size: 11px;
        letter-spacing: 0.04em;
      }

      /* Datum+Zeit: längeres Format, mehr Platz für Icons */
      .wrtk-datefield--datetime .wrtk-input{
        padding-right: 5.25rem;
        font-size: 0.8125rem;
      }
      /* Kompakt + Datum/Zeit: Icons brauchen mehr Platz, sonst wird HH:mm abgeschnitten */
      .wrtk-datefield--datetime.wrtk-datefield--compact .wrtk-input{
        padding-right: 6.5rem;
        font-size: 0.8125rem;
      }
      @media (max-width: 767px) {
        .wrtk-datefield--datetime .wrtk-input{
          padding-right: 3.5rem;
          font-size: 16px;
        }
        .wrtk-datefield--datetime.wrtk-datefield--compact .wrtk-input{
          padding-right: 4.75rem;
          font-size: 0.875rem;
        }
      }

      .react-datepicker__input-container input{
        color:inherit;
        background:transparent;
        border:none;
        width:100%;
        max-width: 100%;
        min-width: 0;
        box-sizing: border-box;
      }

      .wrtk-input[type="date"],
      .wrtk-input[type="datetime-local"],
      .wrtk-input[type="week"],
      .wrtk-input[type="month"]{
        display: block;
        appearance: none;
        -webkit-appearance: none;
      }

      /* Native Picker-Indikatoren ausblenden (wir rendern eigenes Icon/Button) */
      .wrtk-input[type="date"]::-webkit-calendar-picker-indicator,
      .wrtk-input[type="datetime-local"]::-webkit-calendar-picker-indicator,
      .wrtk-input[type="week"]::-webkit-calendar-picker-indicator,
      .wrtk-input[type="month"]::-webkit-calendar-picker-indicator{
        opacity: 0;
        width: 0;
        margin: 0;
      }

      /* ─── Week Picker ─── */
      .rd-cal-week .react-datepicker__day-names{
        display:none !important;
      }
      .rd-cal-week .react-datepicker__month{
        display:block !important;
        grid-template-columns:none !important;
        padding-top: 8px !important;
      }
      .rd-cal-week .react-datepicker__week{
        display:flex !important;
        align-items:center !important;
        justify-content:center !important;
        gap: var(--gap) !important;
      }
      .react-datepicker__week-number{
        width: var(--col) !important;
        height: var(--col) !important;
        margin:0 !important;
        display:flex !important; align-items:center !important; justify-content:center !important;
        color: var(--muted) !important;
        font-weight:600 !important;
        font-size: calc(var(--ui) - 1px) !important;
        border-radius:10px !important;
        border:1px solid transparent !important;
        background:transparent !important;
        cursor:pointer;
      }
      .react-datepicker__week-number:hover{
        background: rgba(255,255,255,.06) !important;
      }
      .react-datepicker__week-number--selected{
        color:#fff !important;
        background: rgba(59,130,246,.18) !important;
        border-color: rgba(147,197,253,.3) !important;
      }

      /* ─── Month Picker ─── */
      .rd-cal-month .react-datepicker__day-names{ display:none !important; }
      .rd-cal-month .react-datepicker__month{
        display:flex !important; flex-direction:column !important;
        grid-template-columns:none !important;
        gap:var(--gap) !important;
        padding:10px var(--padX) !important;
      }
      .react-datepicker__month-wrapper{
        display:flex !important; justify-content:center !important;
        gap:var(--gap) !important;
      }
      .react-datepicker__month-text{
        width:4.5rem !important; height:2rem !important;
        margin:2px !important;
        display:inline-flex !important; align-items:center !important; justify-content:center !important;
        border-radius:10px !important;
        border:1px solid rgba(255,255,255,.06) !important;
        background:rgba(255,255,255,.04) !important;
        color: var(--txt) !important;
        font-size: var(--ui) !important;
        cursor:pointer;
        transition: background .12s, border-color .12s;
      }
      .react-datepicker__month-text:hover{
        background:rgba(255,255,255,.08) !important;
      }
      .react-datepicker__month-text--today{
        box-shadow: 0 0 0 1px rgba(59,130,246,.35) inset !important;
      }
      .react-datepicker__month-text--selected,
      .react-datepicker__month-text--keyboard-selected{
        color:#fff !important;
        background: linear-gradient(135deg, rgba(59,130,246,.32), rgba(59,130,246,.18)) !important;
        border-color: rgba(147,197,253,.45) !important;
        box-shadow: 0 0 6px rgba(59,130,246,.22) !important;
      }

      /* ===== Stabilitaets-Layer (final) =====
         Diese Regeln stehen absichtlich am Ende und korrigieren
         alle vorherigen Layout-Kollisionen (Header/Days/Heute/Klickbarkeit). */
      .rd-cal .react-datepicker__month{
        display:block !important;
        grid-template-columns:none !important;
        padding:8px var(--padX) 10px !important;
      }
      .rd-cal .react-datepicker__week{
        display:flex !important;
        justify-content:center !important;
        align-items:center !important;
        gap: var(--gap) !important;
      }
      .rd-cal .react-datepicker__day-names{
        display:flex !important;
        justify-content:center !important;
        align-items:center !important;
        gap: var(--gap) !important;
        height:auto !important;
        padding:0 var(--padX) 6px !important;
        border-bottom:1px solid rgba(255,255,255,.08) !important;
      }
      .rd-cal .react-datepicker__day-name{
        display:inline-flex !important;
        align-items:center !important;
        justify-content:center !important;
        width: var(--col) !important;
        min-width: var(--col) !important;
        max-width: var(--col) !important;
        height: 18px !important;
        margin:0 !important;
        overflow: hidden !important;
        white-space: nowrap !important;
        text-overflow: clip !important;
        font-size: 11px !important;
        line-height: 1 !important;
        color: var(--muted) !important;
      }
      .rd-cal .react-datepicker__day{
        display:inline-flex !important;
        align-items:center !important;
        justify-content:center !important;
        width: var(--col) !important;
        min-width: var(--col) !important;
        max-width: var(--col) !important;
        height: var(--col) !important;
        margin:0 !important;
        pointer-events: auto !important;
        cursor: pointer !important;
      }
      .rd-cal .react-datepicker__today-button{
        position:static !important;
        width:100% !important;
        height:32px !important;
        display:flex !important;
        align-items:center !important;
        justify-content:center !important;
        margin:0 !important;
        padding:0 !important;
        border-top:1px solid rgba(255,255,255,.10) !important;
        pointer-events:auto !important;
        cursor:pointer !important;
        z-index:auto !important;
      }
      .rd--dateonly .rd-cal{
        padding-bottom:0 !important;
      }
      /* Week-Picker: keine Day-Names-Zeile, damit keine Doppel-Header entstehen */
      .rd-cal-week .react-datepicker__day-names{
        display:none !important;
      }
      .rd-cal-week .react-datepicker__week-number{
        width: var(--col) !important;
        min-width: var(--col) !important;
        max-width: var(--col) !important;
      }

      /* Final: sichere Kürzel nur im normalen Tagespicker */
      .rd-cal:not(.rd-cal-week):not(.rd-cal-month) .react-datepicker__day-name{
        font-size: 0 !important;
        color: transparent !important;
      }
      .rd-cal:not(.rd-cal-week):not(.rd-cal-month) .react-datepicker__day-name::after{
        font-size: 11px !important;
        line-height: 1 !important;
        color: var(--muted) !important;
      }
      .rd-cal:not(.rd-cal-week):not(.rd-cal-month) .react-datepicker__day-name:nth-child(1)::after{ content: "Mo"; }
      .rd-cal:not(.rd-cal-week):not(.rd-cal-month) .react-datepicker__day-name:nth-child(2)::after{ content: "Di"; }
      .rd-cal:not(.rd-cal-week):not(.rd-cal-month) .react-datepicker__day-name:nth-child(3)::after{ content: "Mi"; }
      .rd-cal:not(.rd-cal-week):not(.rd-cal-month) .react-datepicker__day-name:nth-child(4)::after{ content: "Do"; }
      .rd-cal:not(.rd-cal-week):not(.rd-cal-month) .react-datepicker__day-name:nth-child(5)::after{ content: "Fr"; }
      .rd-cal:not(.rd-cal-week):not(.rd-cal-month) .react-datepicker__day-name:nth-child(6)::after{ content: "Sa"; }
      .rd-cal:not(.rd-cal-week):not(.rd-cal-month) .react-datepicker__day-name:nth-child(7)::after{ content: "So"; }

      /* Enterprise fallback: eigener, stabiler Day-Header */
      .rd-cal:not(.rd-cal-week):not(.rd-cal-month) .react-datepicker__day-names{
        display: none !important;
      }
      .rd-cal:not(.rd-cal-week):not(.rd-cal-month) .react-datepicker__month{
        position: relative !important;
        padding-top: 26px !important;
      }
      .rd-cal:not(.rd-cal-week):not(.rd-cal-month) .react-datepicker__month::before{
        content: "Mo  Di  Mi  Do  Fr  Sa  So";
        position: absolute;
        top: 6px;
        left: 50%;
        transform: translateX(-50%);
        white-space: pre;
        font-size: 11px;
        color: var(--muted);
        letter-spacing: 0.12rem;
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
      }
    `}</style>
  );
}

/* ───────── Field Shell ───────── */
const fieldShellIconCls =
  "p-1 rounded-md hover:bg-white/10 text-blue-100/80 !min-h-0 !min-w-0 !h-7 !w-7 !p-1 shrink-0";

const FieldShell = ({
  id,
  label,
  children,
  value,
  onClear,
  onOpen,
  openTriggerRef,
  isOpen = false,
  disabled = false,
}) => {
  const clearCls = fieldShellIconCls;
  const openCls = fieldShellIconCls;
  return (
    <div className="w-full min-w-0 max-w-full">
      {label ? <Label htmlFor={id}>{label}</Label> : null}
      <div className="relative wrtk-datefield-shell-inner">
        <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-0.5 z-[2]">
          <AppButton
            ref={openTriggerRef}
            type="button"
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              if (typeof onOpen === "function") {
                onOpen();
                return;
              }
              const byId = typeof document !== "undefined" ? document.getElementById(id) : null;
              const root =
                e.currentTarget.closest(".wrtk-field-shell") ||
                e.currentTarget.closest(".relative");
              const input = byId || root?.querySelector("input.wrtk-input") || root?.querySelector("input");
              openNativePicker(input);
            }}
            className={openCls}
            aria-label="Kalender öffnen"
            aria-expanded={isOpen}
            aria-haspopup="dialog"
            title="Kalender öffnen"
            disabled={disabled}
          >
            <Calendar className="w-4 h-4" />
          </AppButton>
          {value ? (
            <AppButton
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClear}
              className={clearCls}
              aria-label="Datum löschen"
              title="Löschen"
            >
              <X className="w-4 h-4" />
            </AppButton>
          ) : null}
        </div>
        {children}
      </div>
    </div>
  );
};

function openNativePicker(input) {
  if (!input) return;
  if (typeof input.showPicker === "function") {
    try {
      input.showPicker();
      return;
    } catch {
      // Browser/Policy blockiert showPicker -> fallback
    }
  }
  input.focus();
  input.click();
}

function closePicker(setIsOpen, triggerRef, { focusTrigger = true } = {}) {
  setIsOpen(false);
  if (!focusTrigger) return;
  if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
    window.requestAnimationFrame(() => triggerRef?.current?.focus?.());
    return;
  }
  triggerRef?.current?.focus?.();
}

/* ───────── Header (zentriert) ───────── */
function HeaderSelect({
  date,
  decreaseMonth,
  increaseMonth,
  setMonth,
  setYear,
  yearStart = 1900,
  yearEnd = 2100,
}) {
  const selectCls =
    "bg-transparent text-blue-100/90 border border-white/10 rounded px-2 py-[1px] text-[11px] focus:outline-none focus:ring-2 focus:ring-blue-400/30 h-7";
  const optionCls = "bg-[#0b1324]";
  const months = [
    "Januar", "Februar", "März", "April", "Mai", "Juni",
    "Juli", "August", "September", "Oktober", "November", "Dezember",
  ];
  const years = [];
  for (let y = yearEnd; y >= yearStart; y--) years.push(y);

  return (
    <div className="react-datepicker__header !px-2 !py-1">
      <div className="rd-head">
        <AppButton type="button" variant="ghost" size="sm" onClick={decreaseMonth} className="btn select-none h-7 w-7 !p-0" aria-label="Voriger Monat">‹</AppButton>
        <FormSelect className={selectCls} value={date.getMonth()} onChange={(e) => setMonth(parseInt(e.target.value, 10))} aria-label="Monat wählen">
          {months.map((m, i) => (
            <option key={m} value={i} className={optionCls}>{m}</option>
          ))}
        </FormSelect>
        <FormSelect className={selectCls} value={date.getFullYear()} onChange={(e) => setYear(parseInt(e.target.value, 10))} aria-label="Jahr wählen">
          {years.map((y) => (
            <option key={y} value={y} className={optionCls}>{y}</option>
          ))}
        </FormSelect>
        <AppButton type="button" variant="ghost" size="sm" onClick={increaseMonth} className="btn select-none h-7 w-7 !p-0" aria-label="Nächster Monat">›</AppButton>
      </div>
    </div>
  );
}

/* ───────── Custom Time Input ───────── */
const CustomTimeInput = ({ date, onChange, stepMinutes = 5 }) => {
  const pad2 = (n) => String(n).padStart(2, "0");
  const hh =
    date instanceof Date && !isNaN(date) ? pad2(date.getHours()) : "00";
  const mm =
    date instanceof Date && !isNaN(date) ? pad2(date.getMinutes()) : "00";
  const current = `${hh}:${mm}`;

  return (
    <input
      type="time"
      className="wrtk-time-input"
      value={current}
      onChange={(e) => {
        const v = String(e.target.value || "").slice(0, 5);
        if (/^\d{2}:\d{2}$/.test(v)) onChange(v);
      }}
      step={Math.max(1, stepMinutes) * 60}
      autoComplete="off"
      inputMode="numeric"
    />
  );
};

/* ───────── DateOnly ───────── */
export function DateOnlyField({
  id,
  label,
  value,
  onChange,
  placeholder = "Datum wählen…",
  disabled = false,
  yearStart = 1900,
  yearEnd = 2100,
  minDate,
  maxDate,
  maxDaysBack,
  disableFuture = false,
  inline = false,
  compact = false,
}) {
  const isMobile = useStableMobileDateMode();
  const autoId = useId();
  const inputId = id || `date-only-${autoId}`;
  const nativeInputRef = useRef(null);

  const resolvedMin = minDate ?? (maxDaysBack != null ? ymdMinusDays(maxDaysBack) : undefined);
  const resolvedMax = maxDate ?? (disableFuture ? todayYMD() : undefined);
  const minNorm = resolvedMin ? normalizeYMD(resolvedMin) : undefined;
  const maxNorm = resolvedMax ? normalizeYMD(resolvedMax) : undefined;

  if (isMobile) {
    return (
      <div className={`w-full min-w-0 max-w-full wrtk-field-shell${compact ? " wrtk-datefield--compact" : ""}`}>
        <StyleOverrides />
        <FieldShell
          id={inputId}
          label={label}
          value={value}
          onClear={() => onChange?.("")}
          onOpen={() => openNativePicker(nativeInputRef.current)}
        >
          <input
            id={inputId}
            ref={nativeInputRef}
            type="date"
            className="wrtk-input"
            value={normalizeYMD(value)}
            onChange={(e) => onChange?.(normalizeYMD(e.target.value))}
            disabled={disabled}
            min={minNorm || undefined}
            max={maxNorm || undefined}
            autoComplete="off"
          />
        </FieldShell>
      </div>
    );
  }

  const ymd = useMemo(() => normalizeYMD(value), [value]);
  const selected = useMemo(() => parseDateFlexible(ymd), [ymd]);
  const [isOpen, setIsOpen] = useState(false);
  const openBtnRef = useRef(null);
  const close = (opts) => closePicker(setIsOpen, openBtnRef, opts);

  if (inline) {
    return (
      <div className="w-full">
        {label ? <Label htmlFor={inputId}>{label}</Label> : null}
        <StyleOverrides />
        <DatePicker
          id={inputId}
          inline
          selected={selected}
          onChange={(d) => onChange?.(d ? normalizeYMD(toYMD(d)) : "")}
          locale={dePickerLocale}
          dateFormat="P"
          calendarStartDay={1}
          todayButton="Heute"
          fixedHeight
          disabled={disabled}
          minDate={minNorm ? parseDateFlexible(minNorm) : undefined}
          maxDate={maxNorm ? parseDateFlexible(maxNorm) : undefined}
          calendarClassName="rd-cal"
          showPopperArrow={false}
          renderCustomHeader={(p) => (
            <HeaderSelect
              date={p.monthDate}
              decreaseMonth={p.decreaseMonth}
              increaseMonth={p.increaseMonth}
              setMonth={p.changeMonth}
              setYear={p.changeYear}
              yearStart={yearStart}
              yearEnd={yearEnd}
            />
          )}
        />
      </div>
    );
  }

  return (
    <div className={`w-full min-w-0 max-w-full relative wrtk-field-shell${compact ? " wrtk-datefield--compact" : ""}`}>
      <StyleOverrides />
      <FieldShell
        id={inputId}
        label={label}
        value={value}
        onClear={() => onChange?.("")}
        onOpen={() => setIsOpen(true)}
        openTriggerRef={openBtnRef}
        isOpen={isOpen}
        disabled={disabled}
      >
        <DatePicker
          id={inputId}
          selected={selected}
          onChange={(d) => {
            onChange?.(d ? normalizeYMD(toYMD(d)) : "");
            if (d) close({ focusTrigger: false });
          }}
          open={isOpen}
          onInputClick={() => setIsOpen(true)}
          onFocus={() => setIsOpen(true)}
          onClickOutside={() => close({ focusTrigger: false })}
          onCalendarClose={() => close({ focusTrigger: false })}
          onChangeRaw={(e) => {
            const raw = String(e.target.value || "").trim();
            const ymd = normalizeYMD(raw);
            if (ymd) onChange?.(ymd);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              const raw = String(e.currentTarget.value || "").trim();
              onChange?.(normalizeYMD(raw));
              e.preventDefault();
              close({ focusTrigger: false });
            }
            if (e.key === "Escape") {
              e.preventDefault();
              close();
            }
            if (e.key === "Tab") {
              close({ focusTrigger: false });
            }
          }}
          placeholderText={placeholder}
          locale={dePickerLocale}
          dateFormat="P"
          calendarStartDay={1}
          todayButton="Heute"
          shouldCloseOnSelect
          fixedHeight
          popperPlacement="bottom-start"
          popperClassName="rd-popper rd--dateonly"
          calendarClassName="rd-cal"
          showPopperArrow={false}
          portalId={DATEPICKER_POPPER_PORTAL_ID}
          disabled={disabled}
          minDate={minNorm ? parseDateFlexible(minNorm) : undefined}
          maxDate={maxNorm ? parseDateFlexible(maxNorm) : undefined}
          renderCustomHeader={(p) => (
            <HeaderSelect
              date={p.monthDate}
              decreaseMonth={p.decreaseMonth}
              increaseMonth={p.increaseMonth}
              setMonth={p.changeMonth}
              setYear={p.changeYear}
              yearStart={yearStart}
              yearEnd={yearEnd}
            />
          )}
          className="wrtk-input"
          autoComplete="off"
          aria-haspopup="dialog"
          aria-expanded={isOpen}
        />
      </FieldShell>
    </div>
  );
}

/**
 * Verknüpfte Von/Bis-Logik (YYYY-MM-DD).
 * endOptional: Bis darf leer bleiben (Filter, offenes Enddatum).
 * Sonst: leeres Bis → Start; Bis < Start → Start; war Eintag (Von=Bis) → mitziehen.
 */
export function nextRangeOnStartChange(prevStart, prevEnd, nextStart, opts = {}) {
  const { endOptional = false } = opts;
  const von = normalizeYMD(nextStart);
  if (!von) return { start: "", end: normalizeYMD(prevEnd) || "" };
  const oldVon = normalizeYMD(prevStart);
  const oldBis = normalizeYMD(prevEnd);
  let bis = oldBis;
  if (!bis) {
    if (!endOptional) bis = von;
  } else if (bis < von) {
    bis = von;
  } else if (oldVon && oldBis === oldVon) {
    bis = von;
  }
  return { start: von, end: bis };
}

export function nextRangeOnEndChange(prevStart, prevEnd, nextEnd, opts = {}) {
  const { endOptional = false } = opts;
  const bis = normalizeYMD(nextEnd);
  if (!bis) return { start: normalizeYMD(prevStart) || "", end: "" };
  const oldVon = normalizeYMD(prevStart);
  let von = oldVon;
  if (!von || von > bis) von = bis;
  if (!endOptional && !von) von = bis;
  return { start: von, end: bis };
}

const HOUR_MS = 60 * 60 * 1000;

export { HOUR_MS };

/** Lokales ISO-Minuten-Datum um durationMs verschieben (z. B. +1 h). */
export function addDurationToLocalIsoMinute(iso, durationMs) {
  const von = normalizeLocalIsoMinute(iso);
  if (!von || !durationMs) return von;
  const d = parseDateTimeFlexible(von);
  return d ? normalizeLocalIsoMinute(toLocalIsoMinute(new Date(d.getTime() + durationMs))) : von;
}

/** Verknüpfte Von/Bis-Logik (YYYY-MM-DDTHH:mm). */
export function nextDateTimeRangeOnStartChange(prevStart, prevEnd, nextStart, opts = {}) {
  const { endOptional = false, defaultDurationMs = 0 } = opts;
  const von = normalizeLocalIsoMinute(nextStart);
  if (!von) return { start: "", end: normalizeLocalIsoMinute(prevEnd) || "" };

  const oldVon = normalizeLocalIsoMinute(prevStart);
  const oldBis = normalizeLocalIsoMinute(prevEnd);

  const bumpByDefault = () => {
    if (defaultDurationMs > 0) {
      return addDurationToLocalIsoMinute(von, defaultDurationMs);
    }
    return von;
  };

  if (endOptional) {
    const prevBis = oldBis || "";
    const shouldBump =
      defaultDurationMs > 0 &&
      (!prevBis || prevBis < von || (oldVon && prevBis === oldVon));
    return { start: von, end: shouldBump ? bumpByDefault() : prevBis };
  }

  let bis = oldBis;

  if (!bis) {
    bis = bumpByDefault();
  } else if (bis < von) {
    bis = bumpByDefault();
  } else if (oldVon && oldBis === oldVon) {
    bis = bumpByDefault();
  }
  return { start: von, end: bis };
}

export function nextDateTimeRangeOnEndChange(prevStart, prevEnd, nextEnd, opts = {}) {
  const { endOptional = false } = opts;
  const bis = normalizeLocalIsoMinute(nextEnd);
  if (!bis) return { start: normalizeLocalIsoMinute(prevStart) || "", end: "" };

  if (endOptional) {
    return { start: normalizeLocalIsoMinute(prevStart) || "", end: bis };
  }

  const oldVon = normalizeLocalIsoMinute(prevStart);
  let von = oldVon;
  if (!von || von > bis) von = bis;
  if (!endOptional && !von) von = bis;
  return { start: von, end: bis };
}

function countInclusiveDays(start, end) {
  const s = normalizeYMD(start);
  const e = normalizeYMD(end);
  if (!s || !e) return 0;
  const d0 = parseDateFlexible(s);
  const d1 = parseDateFlexible(e);
  if (!d0 || !d1) return 0;
  const diff = Math.round((d1.getTime() - d0.getTime()) / 86400000);
  return Math.max(1, diff + 1);
}

/**
 * Von/Bis mit automatischer Synchronisation + optional inline Bereichskalender (Desktop).
 * onChange: ({ start, end }) mit YYYY-MM-DD.
 */
export function DateRangeField({
  start,
  end,
  onChange,
  label,
  startLabel = "Von",
  endLabel = "Bis",
  disabled = false,
  yearStart = 1900,
  yearEnd = 2100,
  minDate,
  maxDate,
  disableFuture = false,
  showInlineCalendar = false,
  compact = false,
  endOptional = false,
}) {
  const isMobile = useStableMobileDateMode();
  const startNorm = normalizeYMD(start);
  const endNorm = normalizeYMD(end);
  const resolvedMin = minDate ? normalizeYMD(minDate) : undefined;
  const resolvedMax = maxDate ? normalizeYMD(maxDate) : disableFuture ? todayYMD() : undefined;
  const rangeOpts = useMemo(() => ({ endOptional }), [endOptional]);

  const handleStartInput = (v) => onChange?.(nextRangeOnStartChange(start, end, v, rangeOpts));
  const handleEndInput = (v) => onChange?.(nextRangeOnEndChange(start, end, v, rangeOpts));

  const startDate = useMemo(() => parseDateFlexible(startNorm), [startNorm]);
  const endDate = useMemo(() => parseDateFlexible(endNorm), [endNorm]);
  const dayCount = useMemo(() => countInclusiveDays(startNorm, endNorm), [startNorm, endNorm]);

  const handleCalendarChange = (dates) => {
    const [a, b] = dates || [];
    if (!a) {
      onChange?.({ start: "", end: "" });
      return;
    }
    const s = normalizeYMD(toYMD(a));
    const e = b ? normalizeYMD(toYMD(b)) : s;
    const [startY, endY] = s <= e ? [s, e] : [e, s];
    onChange?.({ start: startY, end: endY });
  };

  const showCal = showInlineCalendar && !isMobile;
  const rangeHint =
    startNorm && endNorm ? (startNorm === endNorm ? "1 Tag" : `${dayCount} Tage`) : null;

  return (
    <div className={compact ? "w-full min-w-0 space-y-1" : "w-full space-y-1.5"}>
      {label && !compact ? <Label>{label}</Label> : null}
      {compact ? (
        <div className="space-y-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <DateOnlyField
              label={startLabel}
              value={startNorm}
              onChange={handleStartInput}
              placeholder="tt.mm.jjjj"
              disabled={disabled}
              yearStart={yearStart}
              yearEnd={yearEnd}
              minDate={resolvedMin}
              maxDate={resolvedMax}
              disableFuture={disableFuture}
              compact
            />
            <DateOnlyField
              label={endLabel}
              value={endNorm}
              onChange={handleEndInput}
              placeholder="tt.mm.jjjj"
              disabled={disabled}
              yearStart={yearStart}
              yearEnd={yearEnd}
              minDate={startNorm || resolvedMin}
              maxDate={resolvedMax}
              disableFuture={disableFuture}
              compact
            />
          </div>
          {rangeHint ? (
            <p className="text-[11px] text-blue-300/60 tabular-nums px-0.5">{rangeHint}</p>
          ) : null}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <DateOnlyField
            label={startLabel}
            value={startNorm}
            onChange={handleStartInput}
            placeholder="tt.mm.jjjj"
            disabled={disabled}
            yearStart={yearStart}
            yearEnd={yearEnd}
            minDate={resolvedMin}
            maxDate={resolvedMax}
            disableFuture={disableFuture}
            compact
          />
          <DateOnlyField
            label={endLabel}
            value={endNorm}
            onChange={handleEndInput}
            placeholder="tt.mm.jjjj"
            disabled={disabled}
            yearStart={yearStart}
            yearEnd={yearEnd}
            minDate={startNorm || resolvedMin}
            maxDate={resolvedMax}
            disableFuture={disableFuture}
            compact
          />
        </div>
      )}
      {!compact && startNorm && endNorm ? (
        <p className="text-[10px] text-muted tabular-nums">
          {startNorm === endNorm ? "1 Tag" : `${dayCount} Tage`}
        </p>
      ) : null}
      {showCal ? (
        <div className="rounded-xl border border-default bg-surface/40 p-2 w-fit max-w-full overflow-x-auto">
          <StyleOverrides />
          <DatePicker
            inline
            selectsRange
            startDate={startDate}
            endDate={endDate}
            selected={startDate}
            onChange={handleCalendarChange}
            locale={dePickerLocale}
            dateFormat="P"
            calendarStartDay={1}
            todayButton="Heute"
            fixedHeight
            disabled={disabled}
            minDate={resolvedMin ? parseDateFlexible(resolvedMin) : undefined}
            maxDate={resolvedMax ? parseDateFlexible(resolvedMax) : undefined}
            calendarClassName="rd-cal"
            showPopperArrow={false}
            renderCustomHeader={(p) => (
              <HeaderSelect
                date={p.monthDate}
                decreaseMonth={p.decreaseMonth}
                increaseMonth={p.increaseMonth}
                setMonth={p.changeMonth}
                setYear={p.changeYear}
                yearStart={yearStart}
                yearEnd={yearEnd}
              />
            )}
          />
        </div>
      ) : null}
    </div>
  );
}

/**
 * Von/Bis Datum+Zeit mit automatischer Synchronisation.
 * onChange: ({ start, end }) mit YYYY-MM-DDTHH:mm.
 */
export function DateTimeRangeField({
  start,
  end,
  onChange,
  label,
  startLabel = "Von",
  endLabel = "Bis",
  disabled = false,
  yearStart = 1900,
  yearEnd = 2100,
  minDate,
  maxDate,
  disableFuture = false,
  compact = false,
  stepMinutes = 5,
  endOptional = false,
  defaultDurationMs = 0,
}) {
  const startNorm = normalizeLocalIsoMinute(start);
  const endNorm = normalizeLocalIsoMinute(end);
  const resolvedMin = minDate ? normalizeYMD(minDate) : undefined;
  const resolvedMax = maxDate ? normalizeYMD(maxDate) : disableFuture ? todayYMD() : undefined;
  const rangeOpts = useMemo(
    () => ({ endOptional, defaultDurationMs }),
    [endOptional, defaultDurationMs]
  );

  const handleStartInput = (v) => onChange?.(nextDateTimeRangeOnStartChange(start, end, v, rangeOpts));
  const handleEndInput = (v) => onChange?.(nextDateTimeRangeOnEndChange(start, end, v, rangeOpts));

  return (
    <div className={compact ? "w-full min-w-0 space-y-1" : "w-full space-y-1.5"}>
      {label && !compact ? <Label>{label}</Label> : null}
      <div className={compact ? "grid grid-cols-1 gap-2" : "grid grid-cols-1 gap-3"}>
        <DateTimeField
          label={startLabel}
          value={startNorm}
          onChange={handleStartInput}
          stepMinutes={stepMinutes}
          disabled={disabled}
          yearStart={yearStart}
          yearEnd={yearEnd}
          minDate={resolvedMin}
          maxDate={resolvedMax}
          disableFuture={disableFuture}
          compact={compact}
        />
        <DateTimeField
          label={endLabel}
          value={endNorm}
          onChange={handleEndInput}
          stepMinutes={stepMinutes}
          disabled={disabled}
          yearStart={yearStart}
          yearEnd={yearEnd}
          minDate={startNorm ? startNorm.slice(0, 10) : resolvedMin}
          maxDate={resolvedMax}
          disableFuture={disableFuture}
          compact={compact}
        />
      </div>
    </div>
  );
}

/* ───────── Date + Time ───────── */
export function DateTimeField({
  id,
  label,
  value,
  onChange,
  placeholder = "Datum & Zeit wählen…",
  stepMinutes = 5,
  disabled = false,
  yearStart = 1900,
  yearEnd = 2100,
  minDate,
  maxDate,
  maxDaysBack,
  disableFuture = false,
  compact = false,
}) {
  const isMobile = useStableMobileDateMode();
  const autoId = useId();
  const inputId = id || `date-time-${autoId}`;
  const nativeInputRef = useRef(null);

  const resolvedMin = minDate ?? (maxDaysBack != null ? ymdMinusDays(maxDaysBack) : undefined);
  const resolvedMax = maxDate ?? (disableFuture ? todayYMD() : undefined);
  const minNorm = resolvedMin ? normalizeYMD(resolvedMin) : undefined;
  const maxNorm = resolvedMax ? normalizeYMD(resolvedMax) : undefined;
  const minAttr = minNorm ? `${minNorm}T00:00` : undefined;
  const maxAttr = maxNorm ? `${maxNorm}T23:59` : undefined;

  if (isMobile) {
    return (
      <div className={`w-full min-w-0 max-w-full wrtk-field-shell wrtk-datefield--datetime${compact ? " wrtk-datefield--compact" : ""}`}>
        <StyleOverrides />
        <FieldShell
          id={inputId}
          label={label}
          value={value}
          onClear={() => onChange?.("")}
          onOpen={() => openNativePicker(nativeInputRef.current)}
        >
          <input
            id={inputId}
            ref={nativeInputRef}
            type="datetime-local"
            className="wrtk-input"
            value={normalizeLocalIsoMinute(value)}
            onChange={(e) => onChange?.(normalizeLocalIsoMinute(e.target.value))}
            disabled={disabled}
            min={minAttr}
            max={maxAttr}
            autoComplete="off"
          />
        </FieldShell>
      </div>
    );
  }

  const selected = useMemo(() => parseDateTimeFlexible(value), [value]);
  const [isOpen, setIsOpen] = useState(false);
  const openBtnRef = useRef(null);
  const close = (opts) => closePicker(setIsOpen, openBtnRef, opts);

  return (
    <div className={`w-full min-w-0 max-w-full relative wrtk-field-shell wrtk-datefield--datetime${compact ? " wrtk-datefield--compact" : ""}`}>
      <StyleOverrides />
      <FieldShell
        id={inputId}
        label={label}
        value={value}
        onClear={() => onChange?.("")}
        onOpen={() => setIsOpen(true)}
        openTriggerRef={openBtnRef}
        isOpen={isOpen}
        disabled={disabled}
      >
        <DatePicker
          id={inputId}
          selected={selected}
          onChange={(d) => onChange?.(d ? normalizeLocalIsoMinute(toLocalIsoMinute(d)) : "")}
          open={isOpen}
          onInputClick={() => setIsOpen(true)}
          onFocus={() => setIsOpen(true)}
          onClickOutside={() => close({ focusTrigger: false })}
          onCalendarClose={() => close()}
          onChangeRaw={(e) => {
            const v = normalizeLocalIsoMinute(e.target.value);
            if (v) onChange?.(v);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              onChange?.(normalizeLocalIsoMinute(e.currentTarget.value));
              e.preventDefault();
              close({ focusTrigger: false });
            }
            if (e.key === "Escape") {
              e.preventDefault();
              close();
            }
            if (e.key === "Tab") {
              close({ focusTrigger: false });
            }
          }}
          placeholderText={placeholder}
          showTimeSelect
          timeIntervals={stepMinutes}
          showTimeInput
          timeInputLabel=""
          customTimeInput={<CustomTimeInput stepMinutes={stepMinutes} />}
          dateFormat={compact ? "dd.MM.yyyy HH:mm" : "P, HH:mm"}
          timeFormat="HH:mm"
          locale={dePickerLocale}
          calendarStartDay={1}
          todayButton="Heute"
          shouldCloseOnSelect={false}
          fixedHeight
          popperPlacement="bottom-start"
          popperClassName="rd-popper rd--dtbar"
          calendarClassName="rd-cal"
          showPopperArrow={false}
          portalId={DATEPICKER_POPPER_PORTAL_ID}
          disabled={disabled}
          minDate={minNorm ? parseDateFlexible(minNorm) : undefined}
          maxDate={maxNorm ? parseDateFlexible(maxNorm) : undefined}
          renderCustomHeader={(p) => (
            <HeaderSelect
              date={p.monthDate}
              decreaseMonth={p.decreaseMonth}
              increaseMonth={p.increaseMonth}
              setMonth={p.changeMonth}
              setYear={p.changeYear}
              yearStart={yearStart}
              yearEnd={yearEnd}
            />
          )}
          className="wrtk-input"
          autoComplete="off"
          aria-haspopup="dialog"
          aria-expanded={isOpen}
        />
      </FieldShell>
    </div>
  );
}

/* ───────── ISO-Week Helpers ───────── */

function isoWeekOf(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - dayNum + 3);
  const firstThu = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  const week = 1 + Math.round(((d - firstThu) / 86400000 - 3 + ((firstThu.getUTCDay() + 6) % 7)) / 7);
  return { year: d.getUTCFullYear(), week };
}

function weekStringFromDate(d) {
  if (!d || isNaN(d.getTime())) return "";
  const { year, week } = isoWeekOf(d);
  return `${year}-W${pad(week)}`;
}

function dateFromWeekString(v) {
  if (!v) return null;
  const m = String(v).match(/^(\d{4})-W(\d{1,2})$/);
  if (!m) return null;
  const year = parseInt(m[1], 10);
  const week = parseInt(m[2], 10);
  const jan4 = new Date(year, 0, 4);
  const dayOfWeek = (jan4.getDay() + 6) % 7;
  const monday = new Date(jan4);
  monday.setDate(jan4.getDate() - dayOfWeek + (week - 1) * 7);
  return monday;
}

/* ───────── Week Picker ───────── */
export function WeekPickerField({
  id,
  label,
  value,
  onChange,
  placeholder = "Woche wählen…",
  disabled = false,
  yearStart = 2000,
  yearEnd = 2100,
}) {
  const isMobile = useStableMobileDateMode();
  const autoId = useId();
  const inputId = id || `week-pick-${autoId}`;
  const nativeInputRef = useRef(null);

  const selected = useMemo(() => dateFromWeekString(value), [value]);
  const [isOpen, setIsOpen] = useState(false);
  const openBtnRef = useRef(null);
  const close = (opts) => closePicker(setIsOpen, openBtnRef, opts);

  const displayValue = useMemo(() => {
    if (!value) return "";
    const m = String(value).match(/^(\d{4})-W(\d{1,2})$/);
    if (!m) return value;
    return `Woche ${pad(parseInt(m[2], 10))}, ${m[1]}`;
  }, [value]);

  if (isMobile) {
    return (
      <div className="w-full min-w-0 max-w-full wrtk-field-shell">
        <StyleOverrides />
        <FieldShell
          id={inputId}
          label={label}
          value={value}
          onClear={() => onChange?.("")}
          onOpen={() => openNativePicker(nativeInputRef.current)}
        >
          <input
            id={inputId}
            ref={nativeInputRef}
            type="week"
            className="wrtk-input"
            value={value || ""}
            onChange={(e) => onChange?.(e.target.value)}
            disabled={disabled}
            autoComplete="off"
          />
        </FieldShell>
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 max-w-full relative wrtk-field-shell">
      <StyleOverrides />
      <FieldShell
        id={inputId}
        label={label}
        value={value}
        onClear={() => onChange?.("")}
        onOpen={() => setIsOpen(true)}
        openTriggerRef={openBtnRef}
        isOpen={isOpen}
        disabled={disabled}
      >
        <DatePicker
          id={inputId}
          selected={selected}
          onChange={(d) => {
            const wk = weekStringFromDate(d);
            if (wk) onChange?.(wk);
            close({ focusTrigger: false });
          }}
          open={isOpen}
          onInputClick={() => setIsOpen(true)}
          onFocus={() => setIsOpen(true)}
          onClickOutside={() => close({ focusTrigger: false })}
          onCalendarClose={() => close()}
          showWeekPicker
          showWeekNumbers
          locale={dePickerLocale}
          calendarStartDay={1}
          todayButton="Diese Woche"
          shouldCloseOnSelect
          fixedHeight
          popperPlacement="bottom-start"
          popperClassName="rd-popper rd--dateonly"
          calendarClassName="rd-cal rd-cal-week"
          showPopperArrow={false}
          portalId={DATEPICKER_POPPER_PORTAL_ID}
          disabled={disabled}
          value={displayValue}
          renderCustomHeader={(p) => (
            <HeaderSelect
              date={p.monthDate}
              decreaseMonth={p.decreaseMonth}
              increaseMonth={p.increaseMonth}
              setMonth={p.changeMonth}
              setYear={p.changeYear}
              yearStart={yearStart}
              yearEnd={yearEnd}
            />
          )}
          className="wrtk-input"
          autoComplete="off"
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault();
              close();
            }
            if (e.key === "Tab") {
              close({ focusTrigger: false });
            }
          }}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
        />
      </FieldShell>
    </div>
  );
}

/* ───────── Month Picker Header (nur Jahr-Navigation) ───────── */
function MonthPickerHeader({
  date,
  decreaseYear,
  increaseYear,
  changeYear,
  yearStart = 2000,
  yearEnd = 2100,
}) {
  const selectCls =
    "bg-transparent text-blue-100/90 border border-white/10 rounded px-2 py-[1px] text-[11px] focus:outline-none focus:ring-2 focus:ring-blue-400/30 h-7";
  const optionCls = "bg-[#0b1324]";
  const years = [];
  for (let y = yearEnd; y >= yearStart; y--) years.push(y);

  return (
    <div className="react-datepicker__header !px-2 !py-1">
      <div className="rd-head">
        <AppButton type="button" variant="ghost" size="sm" onClick={decreaseYear} className="btn select-none h-7 w-7 !p-0" aria-label="Voriges Jahr">‹</AppButton>
        <FormSelect
          className={selectCls}
          value={date.getFullYear()}
          onChange={(e) => changeYear(parseInt(e.target.value, 10))}
          aria-label="Jahr wählen"
        >
          {years.map((y) => (
            <option key={y} value={y} className={optionCls}>{y}</option>
          ))}
        </FormSelect>
        <AppButton type="button" variant="ghost" size="sm" onClick={increaseYear} className="btn select-none h-7 w-7 !p-0" aria-label="Nächstes Jahr">›</AppButton>
      </div>
    </div>
  );
}

/* ───────── Month Picker ───────── */
const DE_MONTHS = [
  "Januar","Februar","März","April","Mai","Juni",
  "Juli","August","September","Oktober","November","Dezember",
];

export function MonthPickerField({
  id,
  label,
  value,
  onChange,
  placeholder = "Monat wählen…",
  disabled = false,
  yearStart = 2000,
  yearEnd = 2100,
}) {
  const isMobile = useStableMobileDateMode();
  const autoId = useId();
  const inputId = id || `month-pick-${autoId}`;
  const nativeInputRef = useRef(null);

  const selected = useMemo(() => {
    if (!value) return null;
    const [y, m] = value.split("-").map(Number);
    if (!y || !m) return null;
    return new Date(y, m - 1, 1);
  }, [value]);
  const [isOpen, setIsOpen] = useState(false);
  const openBtnRef = useRef(null);
  const close = (opts) => closePicker(setIsOpen, openBtnRef, opts);

  const displayValue = useMemo(() => {
    if (!selected || isNaN(selected.getTime())) return "";
    return `${DE_MONTHS[selected.getMonth()]} ${selected.getFullYear()}`;
  }, [selected]);

  if (isMobile) {
    return (
      <div className="w-full min-w-0 max-w-full wrtk-field-shell">
        <StyleOverrides />
        <FieldShell
          id={inputId}
          label={label}
          value={value}
          onClear={() => onChange?.("")}
          onOpen={() => openNativePicker(nativeInputRef.current)}
        >
          <input
            id={inputId}
            ref={nativeInputRef}
            type="month"
            className="wrtk-input"
            value={value || ""}
            onChange={(e) => onChange?.(e.target.value)}
            disabled={disabled}
            autoComplete="off"
          />
        </FieldShell>
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 max-w-full relative wrtk-field-shell">
      <StyleOverrides />
      <FieldShell
        id={inputId}
        label={label}
        value={value}
        onClear={() => onChange?.("")}
        onOpen={() => setIsOpen(true)}
        openTriggerRef={openBtnRef}
        isOpen={isOpen}
        disabled={disabled}
      >
        <DatePicker
          id={inputId}
          selected={selected}
          onChange={(d) => {
            if (d) onChange?.(`${d.getFullYear()}-${pad(d.getMonth() + 1)}`);
            close({ focusTrigger: false });
          }}
          open={isOpen}
          onInputClick={() => setIsOpen(true)}
          onFocus={() => setIsOpen(true)}
          onClickOutside={() => close({ focusTrigger: false })}
          onCalendarClose={() => close()}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault();
              close();
            }
            if (e.key === "Tab") {
              close({ focusTrigger: false });
            }
          }}
          showMonthYearPicker
          locale={dePickerLocale}
          dateFormat="MMMM yyyy"
          todayButton="Dieser Monat"
          shouldCloseOnSelect
          popperPlacement="bottom-start"
          popperClassName="rd-popper rd--dateonly"
          calendarClassName="rd-cal rd-cal-month"
          showPopperArrow={false}
          portalId={DATEPICKER_POPPER_PORTAL_ID}
          disabled={disabled}
          value={displayValue}
          renderCustomHeader={(p) => (
            <MonthPickerHeader
              date={p.monthDate || p.date}
              decreaseYear={p.decreaseYear}
              increaseYear={p.increaseYear}
              changeYear={p.changeYear}
              yearStart={yearStart}
              yearEnd={yearEnd}
            />
          )}
          className="wrtk-input"
          autoComplete="off"
          aria-haspopup="dialog"
          aria-expanded={isOpen}
        />
      </FieldShell>
    </div>
  );
}
